import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnInit,
  signal,
  effect,
} from '@angular/core';
import { EstacionService } from '../../../sensores/gestion-estaciones/services/gestion-estaciones.service';
import { take, tap } from 'rxjs/operators';
import { ButtonPrimaryComponent } from '../../../../shared/components/button-primary/button-primary.component';
import { ButtonSecondaryComponent } from '../../../../shared/components/button-secondary/button-secondary.component';
import { LoadSpinnerComponent } from '../../../../shared/components/load-spinner/load-spinner.component';
import { Observable, forkJoin, of } from 'rxjs';
import { Layer } from 'leaflet';
import { Router } from '@angular/router';
import { MetricasService } from '../../../sensores/metricas_v2/services/metricas.service';
import { IdEstacionService } from '../../../sensores/gestion-estaciones/services/id_estacion.service';
import { SensoresService } from '../../../sensores/services/sensores.service';
import { VisorgisService } from '../../services/visorgis.service';
import { FormatNumberPipe } from '../../../../shared/pipes/number-separator.pipe';
import { NoTranslateMaterialSymbolsDirective } from '../../../../shared/directives/noTranslateMaterialSymbols.directive';
import { LicensedDirective } from '../../../../shared/directives/licensed.directive';
import { AuthService } from '../../../login/services/auth.service';
import { CollapsiblePopupShellComponent } from '../../../../shared/components/collapsible-popup-shell/collapsible-popup-shell.component';
import {
  EntityPopup,
  EntityType,
  PopupInfoItem,
  EstacionPopup,
  MeteorologicaPopup,
  HumedadPopup,
  NutrientesPopup,
  PuntoLisimetroPopup,
  CabezalPopup,
  PozoPopup,
  PozoBadgeItem,
  HidrantePopup,
  BalsaPopup,
  TomaPopup,
  ParcelaPopup,
  DispositivoEnergiaPopup,
} from './interfaces';
import {
  getSectorLabel,
  getSectorLabelFromCollection,
} from '../../utils/sector.utils';
import { EnergiaService } from '../../../energia/services/energia.service';
import { EnergyPanelNavigationService } from '../../../energia/services/energy-panel-navigation.service';
import { GestionDispositivosModalService } from '../../../energia/services/gestion-dispositivos-modal.service';
import type { EnergyDevice } from '../../../energia/models/energy-device.model';

/**
 * Configuración para cada tipo de entidad del popup.
 * Define URLs, comportamiento visual y cómo obtener/crear el popup.
 */
interface EntityConfig {
  /** URL del botón primario (métricas/informes) */
  primaryUrl: string;
  /** URL del botón secundario (detalles) */
  secondaryUrl: string;
  /** Si se muestra el badge superior */
  showBadge: boolean;
  /** Si se muestra el chip de estado */
  showStatusChip: boolean;
  /** Prefijo para el título (ej: "Estación", "Toma") */
  titlePrefix: string;
  /** Tipo de entidad en Odoo para la navegación */
  odooType?: string;
  /** Si el botón secundario depende del permiso view_odoo_details */
  requiresOdooPermissionForSecondary?: boolean;
  /** Si se muestra la línea de sector en el badge */
  showSectorBadge?: boolean;
  /** Función para obtener los datos de la entidad */
  fetchData: () => Observable<any>;
  /** Función para crear el popup a partir de los datos */
  createPopup: (entity: any) => EntityPopup | null;
}

@Component({
  selector: 'app-marker-popup',
  imports: [
    CollapsiblePopupShellComponent,
    ButtonPrimaryComponent,
    ButtonSecondaryComponent,
    LoadSpinnerComponent,
    NoTranslateMaterialSymbolsDirective,
    LicensedDirective,
  ],
  providers: [EstacionService, DatePipe, FormatNumberPipe],
  templateUrl: './markerPopup.component.html',
  styleUrl: './markerPopup.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarkerPopupComponent implements OnInit {
  @Input() properties: any;
  @Input() popup: Layer;
  @Input() titulo: string;

  // Servicios inyectados
  private estacionesService = inject(EstacionService);
  private changeDetector = inject(ChangeDetectorRef);
  private router = inject(Router);
  private metricasService = inject(MetricasService);
  private idEstacionService = inject(IdEstacionService);
  private sensoresService = inject(SensoresService);
  private visorgisService = inject(VisorgisService);
  private formatNumber = inject(FormatNumberPipe);
  private authService = inject(AuthService);
  private energiaService = inject(EnergiaService);
  private energyPanelNavigation = inject(EnergyPanelNavigationService);
  private gestionDispositivosModalService = inject(
    GestionDispositivosModalService
  );

  // Estado del componente
  isLoading = signal(true);
  showData = signal(false);
  showBadge = signal(true);
  showStatusChip = signal(true);
  isExpanded = signal(true);

  // Datos de la entidad y popup
  entity = signal<any>(null);
  entityPopup = signal<EntityPopup | null>(null);

  // Datos para el template
  badgeText = signal<string>('');
  infoItems = signal<PopupInfoItem[]>([]);
  waterSuppliesList = signal<{ value: string; dateRange: string }[]>([]);
  parcelaBadgeItems = signal<
    { partner: string; location: string; refCatastral: string }[]
  >([]);
  pozoBadgeItem = signal<PozoBadgeItem | null>(null);
  counterInfo = signal<{ title: string; badge: string } | null>(null);
  energyBadgeLines = signal<string[]>([]);
  hideSectorBadge = signal(false);
  sectorLabel = signal('-');

  // Configuración de botones
  label_primary = 'Ver métricas históricas';
  label_secondary = 'Ver detalles';
  redirectPrimaryButtonToURL = signal<string | null>(null);
  redirectSecondaryButtonToURL = signal<string | null>(null);
  primaryAction = signal<(() => void) | null>(null);
  secondaryAction = signal<(() => void) | null>(null);
  requiresOdooPermissionForSecondary = signal(true);

  hasViewOdooDetailsPermission =
    this.authService.hasPermission('view_odoo_details');

  constructor() {
    effect(() => {
      // Cada vez que cambie el estado de expansión, actualizamos el popup de Leaflet
      // para que se ajuste al nuevo tamaño del contenido
      this.isExpanded();
      setTimeout(() => {
        if (this.popup) {
          (this.popup as any).getPopup()?.update();
        }
      }, 310); // Esperamos a que termine la animación del shell (300ms)
    });
  }

  onShellToggled(expanded: boolean): void {
    this.isExpanded.set(expanded);
  }

  ngOnInit(): void {}

  initPopup(): Observable<any> {
    this.isLoading.set(true);
    this.sectorLabel.set('-');
    const type = this.properties.type as EntityType;

    const config = this.getEntityConfig(type);
    if (!config) {
      console.error(`Tipo de entidad no soportado: ${type}`);
      return of(null);
    }

    this.applyConfig(config);

    return config.fetchData().pipe(
      tap((data: any) => {
        this.processEntityData(data, config);
      })
    );
  }

  private getEntityConfig(type: EntityType): EntityConfig | null {
    const configs: Record<EntityType, EntityConfig> = {
      estacion: {
        primaryUrl: '/sensores/metricas',
        secondaryUrl: '/sensores/gestion-estaciones',
        showBadge: true,
        showStatusChip: true,
        titlePrefix: 'Estación',
        fetchData: () => this.fetchEstacionData(),
        createPopup: entity => this.createEstacionPopup(entity),
      },
      toma: {
        primaryUrl: '/hidraulica/informes-de-riego',
        secondaryUrl: '/gestion',
        showBadge: true,
        showStatusChip: true,
        titlePrefix: 'Toma',
        odooType: 'toma',
        fetchData: () =>
          this.visorgisService.getTomaById(this.properties.id).pipe(take(1)),
        createPopup: entity => new TomaPopup(entity),
      },
      pozo: {
        primaryUrl: '/hidraulica/informes-de-riego',
        secondaryUrl: '/gestion',
        showBadge: true,
        showStatusChip: true,
        titlePrefix: 'Pozo',
        odooType: 'pozo',
        fetchData: () =>
          this.visorgisService.getPozoById(this.properties.id).pipe(take(1)),
        createPopup: entity => new PozoPopup(entity),
      },
      hidrante: {
        primaryUrl: '/hidraulica/informes-de-riego',
        secondaryUrl: '/gestion',
        showBadge: true,
        showStatusChip: true,
        titlePrefix: 'Hidrante',
        odooType: 'hidrante',
        fetchData: () =>
          this.visorgisService
            .getHidranteById(this.properties.id)
            .pipe(take(1)),
        createPopup: entity =>
          new HidrantePopup(entity, () =>
            this.visorgisService.get_parcelas_por_hidrante(entity.id)
          ),
      },
      balsa: {
        primaryUrl: '/hidraulica/informes-de-riego',
        secondaryUrl: '/gestion',
        showBadge: false,
        showStatusChip: true,
        titlePrefix: 'Balsa',
        odooType: 'balsas',
        fetchData: () =>
          this.visorgisService.getBalsaById(this.properties.id).pipe(take(1)),
        createPopup: entity => new BalsaPopup(entity),
      },
      cabezal: {
        primaryUrl: '/hidraulica/informes-de-riego',
        secondaryUrl: '/gestion',
        showBadge: false,
        showStatusChip: true,
        titlePrefix: 'Cabezal',
        odooType: 'cabezal',
        fetchData: () =>
          this.visorgisService.getCabezalById(this.properties.id).pipe(take(1)),
        createPopup: entity => new CabezalPopup(entity),
      },
      parcela: {
        primaryUrl: '',
        secondaryUrl: '/gestion',
        showBadge: true,
        showStatusChip: false,
        titlePrefix: 'Parcela',
        odooType: 'parcela',
        fetchData: () =>
          this.visorgisService
            .getParcelaByRefCat(this.properties.ref_catastral)
            .pipe(take(1)),
        createPopup: entities => new ParcelaPopup(entities),
      },
      dispositivo_energia: {
        primaryUrl: '/energia/panel-control',
        secondaryUrl: '/energia/gestion-dispositivos',
        showBadge: true,
        showStatusChip: true,
        titlePrefix: '',
        requiresOdooPermissionForSecondary: false,
        showSectorBadge: false,
        fetchData: () =>
          this.energiaService
            .getDispositivoById(this.properties.id)
            .pipe(take(1)),
        createPopup: entity => new DispositivoEnergiaPopup(entity),
      },
    };

    return configs[type] || null;
  }

  private applyConfig(config: EntityConfig): void {
    this.redirectPrimaryButtonToURL.set(config.primaryUrl);
    this.redirectSecondaryButtonToURL.set(config.secondaryUrl);
    this.showBadge.set(config.showBadge);
    this.showStatusChip.set(config.showStatusChip);
    this.requiresOdooPermissionForSecondary.set(
      config.requiresOdooPermissionForSecondary ?? true
    );
    this.hideSectorBadge.set(config.showSectorBadge === false);
    this.energyBadgeLines.set([]);

    if (this.properties.type === 'estacion') {
      this.properties.status =
        this.properties.state === 'active' ? 'Conectado' : 'Sin conexión';
      this.primaryAction.set(() => this.redireccionarATiempoReal());
      this.secondaryAction.set(() => this.redireccionarAGestionEstaciones());
    } else if (this.properties.type === 'dispositivo_energia') {
      this.properties.status =
        this.properties.state === 'active' ? 'Conectado' : 'Sin conexión';
      this.primaryAction.set(() => this.redireccionarAMetricasEnergia());
      this.secondaryAction.set(() => this.redireccionarADetallesEnergia());
    } else {
      this.primaryAction.set(
        config.primaryUrl ? () => this.redireccionarAHidraulica() : null
      );
      this.secondaryAction.set(() =>
        this.visorgisService.goToOdoo(this.properties.id, config.odooType!)
      );
    }
  }

  private fetchEstacionData(): Observable<any> {
    const getDetalles = this.estacionesService
      .getEstacionById(this.properties.id)
      .pipe(take(1));
    const getHistorico = this.estacionesService
      .getUltimasMedidas(this.properties.id)
      .pipe(take(1));

    return forkJoin([getDetalles, getHistorico]).pipe(
      tap(([detalles, historico]: any[]) => {
        let entity = detalles;
        if (historico.status === '00') {
          // Merge personalizado: tomar todos los datos de sensores del histórico
          // y los metadatos (station_type, provider, etc.) de detalles
          entity = {};

          // Primero copiar todo de historico (datos de sensores)
          Object.keys(historico).forEach(key => {
            entity[key] = historico[key];
          });

          // Luego sobrescribir con detalles, pero solo valores no null/undefined
          Object.keys(detalles).forEach(key => {
            if (
              detalles[key] !== null &&
              detalles[key] !== undefined &&
              detalles[key] !== ''
            ) {
              entity[key] = detalles[key];
            }
          });
        }
        this.entity.set(entity);
      })
    );
  }

  private processEntityData(data: any, config: EntityConfig): void {
    let entityData: any;

    if (this.properties.type === 'estacion') {
      entityData = this.entity();
    } else if (this.properties.type === 'dispositivo_energia') {
      entityData = data;
      this.entity.set(entityData);
      this.properties.status = entityData.is_connected
        ? 'Conectado'
        : 'Sin conexión';
    } else if (this.properties.type === 'parcela') {
      entityData = this.getSelectedParcelaFeatures(data?.features ?? []);
      this.entity.set(entityData);
    } else {
      entityData = data.features[0].properties;
      this.entity.set(entityData);
      this.properties.status = this.getEntityState(entityData.state);
    }

    const popup = config.createPopup(entityData);
    this.entityPopup.set(popup);
    this.sectorLabel.set(this.resolveSectorLabel(entityData));

    if (popup) {
      this.badgeText.set(popup.getBadgeText());
      this.infoItems.set(popup.getInfoItems());

      // Manejar casos especiales para Toma
      if (popup instanceof TomaPopup) {
        this.waterSuppliesList.set(popup.getWaterSuppliesList());
        const counterTitle = popup.getCounterTitle();
        const counterBadge = popup.getCounterBadge();
        if (counterTitle && counterBadge) {
          this.counterInfo.set({ title: counterTitle, badge: counterBadge });
        }
      }

      // Manejar casos especiales para Pozo
      if (popup instanceof PozoPopup) {
        this.pozoBadgeItem.set(popup.getBadgeItems());
      }

      // Manejar casos especiales para Parcela
      if (popup instanceof ParcelaPopup) {
        this.parcelaBadgeItems.set(popup.getBadgeItems());
        this.titulo = popup.getTitle();
      } else if (popup instanceof DispositivoEnergiaPopup) {
        this.energyBadgeLines.set(popup.getBadgeLines());
        this.titulo = popup.getTitle();
      } else {
        this.titulo = `${config.titlePrefix} ${this.properties.name}`;
      }
    }

    this.showData.set(true);
    this.isLoading.set(false);
    this.changeDetector.detectChanges();
  }

  private getSelectedParcelaFeatures(features: any[]): any[] {
    if (!Array.isArray(features) || features.length === 0) {
      return [];
    }

    const parcelaId = this.normalizeNumber(this.properties.id);

    if (parcelaId != null) {
      const featureById = features.find(
        feature => this.normalizeNumber(feature?.properties?.id) === parcelaId
      );

      if (featureById) {
        return [featureById];
      }
    }

    const selectedSectorName = this.getSelectedParcelaSectorName();
    const selectedRefCatastral = this.normalizeString(
      this.properties.ref_catastral
    );
    const selectedPolygonNumber = this.normalizeString(
      this.properties.polygon_number ?? this.properties.poligono_id
    );
    const selectedPlotNumber = this.normalizeString(
      this.properties.plot_number
    );

    const filteredFeatures = features.filter(feature => {
      const featureProperties = feature?.properties;

      if (
        selectedRefCatastral &&
        this.normalizeString(featureProperties?.ref_catastral) !==
          selectedRefCatastral
      ) {
        return false;
      }

      if (
        selectedPolygonNumber &&
        this.normalizeString(
          featureProperties?.polygon_number ?? featureProperties?.poligono_id
        ) !== selectedPolygonNumber
      ) {
        return false;
      }

      if (
        selectedPlotNumber &&
        this.normalizeString(featureProperties?.plot_number) !==
          selectedPlotNumber
      ) {
        return false;
      }

      if (
        selectedSectorName &&
        this.normalizeString(featureProperties?.sector_name) !==
          selectedSectorName
      ) {
        return false;
      }

      return true;
    });

    return filteredFeatures.length > 0 ? [filteredFeatures[0]] : [features[0]];
  }

  private getSelectedParcelaSectorName(): string | null {
    const sectorLabel = getSectorLabel(
      this.properties,
      this.visorgisService.getSectorsMap()
    );

    return sectorLabel === '-' ? null : sectorLabel;
  }

  private normalizeString(value: unknown): string | null {
    if (typeof value !== 'string') {
      if (typeof value === 'number') {
        return String(value);
      }

      return null;
    }

    const trimmedValue = value.trim();
    return trimmedValue.length > 0 ? trimmedValue : null;
  }

  private normalizeNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const trimmedValue = value.trim();

      if (!trimmedValue) {
        return null;
      }

      const parsedValue = Number(trimmedValue);
      return Number.isFinite(parsedValue) ? parsedValue : null;
    }

    return null;
  }

  private resolveSectorLabel(entityData: any): string {
    const sectorsById = this.visorgisService.getSectorsMap();

    if (this.properties.type === 'parcela' && Array.isArray(entityData)) {
      return getSectorLabelFromCollection(entityData, sectorsById);
    }

    const entitySectorLabel = getSectorLabel(entityData, sectorsById);

    if (entitySectorLabel !== '-') {
      return entitySectorLabel;
    }

    return getSectorLabel(this.properties, sectorsById);
  }

  private createEstacionPopup(entity: any): EstacionPopup | null {
    const popupMap: Record<string, new (entity: any) => EstacionPopup> = {
      Meteorológica: MeteorologicaPopup,
      Humedad: HumedadPopup,
      Nutrientes: NutrientesPopup,
      Lisímetro: PuntoLisimetroPopup,
    };

    const PopupClass = popupMap[entity.station_type];
    if (!PopupClass) {
      console.error('No se pudo crear el popup de la estación');
      return null;
    }

    return new PopupClass(entity);
  }

  getEntityState(state: string): string {
    const stateMap: Record<string, string> = {
      active: 'Conectado',
      inactive: 'Sin conexión',
      without_counter: 'Sin contador inteligente',
    };
    return stateMap[state] || '';
  }

  formatValue(item: PopupInfoItem): string {
    if (item.value == null) return '-';

    let result: string;

    // Si skipNumberFormat es true, mostrar el valor como string sin formatear
    if (item.skipNumberFormat) {
      result = String(item.value);
    } else if (typeof item.value === 'number') {
      // Solo formatear como número si es un número o string numérico
      result = this.formatNumber.transform(
        item.value,
        item.maximumFractionDigits
      );
    } else if (
      typeof item.value === 'string' &&
      !isNaN(parseFloat(item.value)) &&
      isFinite(Number(item.value))
    ) {
      result = this.formatNumber.transform(
        item.value,
        item.maximumFractionDigits
      );
    } else {
      result = String(item.value);
    }

    if (item.unit) {
      result += ` ${item.unit}`;
    }

    return result;
  }

  onResize(event: Event): void {
    this.popup.closePopup().openPopup();
  }

  redireccionarATiempoReal(): void {
    if (this.properties?.id) {
      this.metricasService.idEstacion.next(this.properties.id);
      this.router.navigate([this.redirectPrimaryButtonToURL()]);
    } else {
      console.error('No se encontró un valor válido para properties.id');
    }
  }

  redireccionarAHidraulica(): void {
    this.router.navigate([this.redirectPrimaryButtonToURL()]);
  }

  redireccionarAGestionEstaciones(): void {
    if (this.properties?.id) {
      this.idEstacionService.changeIdStationEstaciones(this.properties.id);
      this.sensoresService.ruteRedirect.next('gestion-estaciones');
      this.router.navigate([this.redirectSecondaryButtonToURL()]);
    } else {
      console.error('No se encontró un valor válido para properties.id');
    }
  }

  redireccionarAMetricasEnergia(): void {
    const device = this.entity() as EnergyDevice | null;

    if (!device) {
      console.error('No se encontró el dispositivo de energía para navegar');
      return;
    }

    this.energyPanelNavigation.requestDeviceMetrics(device);
    this.router.navigate([this.redirectPrimaryButtonToURL()]);
  }

  redireccionarADetallesEnergia(): void {
    if (!this.properties?.id) {
      console.error('No se encontró un valor válido para properties.id');
      return;
    }

    this.gestionDispositivosModalService.requestDeviceView(this.properties.id);
    this.router.navigate([this.redirectSecondaryButtonToURL()]);
  }

  onPrimaryClick(): void {
    const action = this.primaryAction();
    if (action) action();
  }

  onSecondaryClick(): void {
    const action = this.secondaryAction();
    if (action) action();
  }

  onInfoItemClick(item: PopupInfoItem): void {
    if (item.isClickable && item.clickAction) {
      item.clickAction();
    }
  }
}
